import * as React from 'react'
import { ChevronRight, Eye, Loader2, RotateCcw } from 'lucide-react'
import {
  EDITABLE_PROSE_SECTIONS,
  PROSE_SECTION_HINTS,
  PROSE_SECTION_LABELS,
  type EditableProseSection,
  type ProposalTemplateOverrides,
  type ProseCopyDto,
  type TemplateSource,
  type TemplateSourceKey,
} from '@lib/dto/proposal-template.dto'
import {
  JOB_TYPES,
  JOB_TYPE_LABELS,
  type JobType,
} from '@lib/enums/job-type.enum'
import type { ProposalWork } from '@lib/enums/proposal-work.enum'
import type { ProposalDocumentDto } from '@lib/dto/proposal-document.dto'
import {
  useProposalTemplate,
  useSaveProposalTemplate,
  usePreviewProposalTemplate,
} from '@/hooks/useProposalTemplate'
import { ProposalDocument } from '@/proposal-document/ProposalDocument'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { errorMessage } from '@/helpers/logger'
import { cn } from '@/lib/utils'

/** The same long-text control the other settings forms use. */
const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-default bg-surface px-2.5 py-1.5 text-sm text-primary-text transition-colors placeholder:text-disabled-text hover:border-strong focus-visible:outline-none'

/**
 * One paragraph or bullet per line. A blank line is not an item, which is
 * how one is removed.
 *
 * Joined with a single newline, matching the hint on the field. A blank line
 * between items used to be inserted on the way back into the textarea, which
 * ate the newline somebody had just typed before the next item could start.
 */
function toLines(values: string[]): string {
  return values.join('\n')
}

function fromLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

/**
 * Paragraphs or bullets while they are being typed.
 *
 * The stored list has no blank lines, but the field has to keep the newline
 * that starts the next item. Writing the textarea from the stored list on
 * every keystroke deletes that newline before the next word, so a second
 * paragraph can never be added by typing — only by pasting.
 */
function LineField({
  id,
  label,
  hint,
  rows,
  lines,
  onLines,
}: {
  id: string
  label: string
  hint: string
  rows: number
  lines: string[]
  onLines: (next: string[]) => void
}) {
  const incoming = toLines(lines)
  // What the stored list last looked like as text. Our own keystrokes set
  // this to the parsed form, so a parent render does not snap the caret
  // back and drop the newline still being typed. A Reset, or a template
  // reload, changes the stored list to something else and the field follows.
  const [echo, setEcho] = React.useState(incoming)
  const [text, setText] = React.useState(incoming)
  if (incoming !== echo) {
    setEcho(incoming)
    setText(incoming)
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <textarea
        id={id}
        rows={rows}
        className={TEXTAREA_CLASS}
        value={text}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
          const next = event.target.value
          setText(next)
          const parsed = fromLines(next)
          setEcho(toLines(parsed))
          onLines(parsed)
        }}
        onBlur={() => {
          const normalized = toLines(fromLines(text))
          setEcho(normalized)
          setText(normalized)
        }}
      />
      <p className="text-xs text-muted-text">{hint}</p>
    </div>
  )
}

/**
 * Where a section's wording came from, said plainly.
 *
 * The distinction decides whether editing here affects one client or every
 * future one, so it is worth a label rather than leaving somebody to infer it
 * from whether the text looks familiar.
 */
function SourceTag({ source }: { source: TemplateSource | undefined }) {
  if (source === 'proposal') {
    return (
      <span className="rounded-full bg-pill-green px-2 py-0.5 text-xs">
        Changed for this proposal
      </span>
    )
  }
  if (source === 'organization') {
    return (
      <span className="rounded-full bg-subtle px-2 py-0.5 text-xs text-secondary-text">
        Your standard wording
      </span>
    )
  }
  return (
    <span className="rounded-full bg-subtle px-2 py-0.5 text-xs text-muted-text">
      Default
    </span>
  )
}

/**
 * A section of the editor that opens on demand.
 *
 * Collapsed by default. Ten sections of heading, paragraphs and bullets laid
 * out in full is a page measured in screens, and almost every visit is to
 * change one of them. Closed, the list reads as a table of contents with each
 * section's source beside it, which is the question somebody arrives with.
 */
function Disclosure({
  title,
  hint,
  aside,
  open,
  onToggle,
  children,
}: {
  title: string
  hint: string
  aside: React.ReactNode
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="rounded-md border border-default">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">
            <button
              type="button"
              aria-expanded={open}
              onClick={onToggle}
              className="flex items-start gap-2 text-left"
            >
              <ChevronRight
                className={cn(
                  'mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-text transition-transform',
                  open ? 'rotate-90' : null,
                )}
              />
              {title}
            </button>
          </h3>
          {open ? (
            <p className="mt-0.5 pl-5.5 text-xs text-muted-text">{hint}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">{aside}</div>
      </div>
      {open ? (
        <div className="space-y-2 border-t border-default p-3">{children}</div>
      ) : null}
    </div>
  )
}

/** One prose section: a heading, paragraphs, and bullets. */
function SectionEditor({
  idPrefix,
  section,
  value,
  source,
  onChange,
  onReset,
  canReset,
  open,
  onToggle,
}: {
  /** Unique to this editor, since Settings keeps several mounted at once. */
  idPrefix: string
  section: EditableProseSection
  value: ProseCopyDto
  source: TemplateSource | undefined
  onChange: (next: ProseCopyDto) => void
  onReset: () => void
  canReset: boolean
  open: boolean
  onToggle: () => void
}) {
  return (
    <Disclosure
      title={PROSE_SECTION_LABELS[section]}
      hint={PROSE_SECTION_HINTS[section]}
      open={open}
      onToggle={onToggle}
      aside={
        <>
          <SourceTag source={source} />
          {canReset ? (
            <Button size="sm" variant="ghost" onClick={onReset}>
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-${section}-title`}>Heading</Label>
        <Input
          id={`${idPrefix}-${section}-title`}
          value={value.title ?? ''}
          onChange={(event) => {
            const next = event.target.value
            onChange({ ...value, title: next === '' ? null : next })
          }}
        />
      </div>

      <LineField
        id={`${idPrefix}-${section}-paragraphs`}
        label="Paragraphs"
        hint="One paragraph per line."
        rows={5}
        lines={value.paragraphs}
        onLines={(paragraphs) => {
          onChange({ ...value, paragraphs })
        }}
      />

      <LineField
        id={`${idPrefix}-${section}-bullets`}
        label="Bullets"
        hint="One bullet per line. Leave empty for none."
        rows={4}
        lines={value.bullets}
        onLines={(bullets) => {
          onChange({ ...value, bullets })
        }}
      />
    </Disclosure>
  )
}

/**
 * Edit the wording a proposal is built from, and see it before committing.
 *
 * Serves both levels from one component: without a `proposalId` it edits the
 * business's standard wording, with one it edits that proposal's changes and
 * shows the standard wording underneath. The shapes are identical, and two
 * editors would have been two places to fix the next thing found wrong.
 *
 * The editor works on RESOLVED wording — what will actually render — rather
 * than on the sparse overrides underneath. An editor showing "nothing set" for
 * a section a client will nevertheless read is an editor that hides the thing
 * it is for. Which level a value came from is said beside it instead, and
 * Reset drops back to the level below rather than blanking the text.
 *
 * The business keeps one set for each kind of proposal, and `work` picks the
 * set at that level. One proposal's editor ignores it: the server answers
 * with the set for what the proposal bids.
 */
export function ProposalWordingEditor({
  proposalId,
  work = 'TREES',
  title,
  blurb,
  embedded = false,
}: {
  proposalId?: string
  /** Which of the business's sets to edit, when there is no proposal. */
  work?: ProposalWork
  title: string
  blurb: string
  /** Inside another panel, which already supplies the border. */
  embedded?: boolean
}) {
  const idPrefix = React.useId()
  const template = useProposalTemplate(proposalId, work)
  const save = useSaveProposalTemplate(proposalId, work)
  const preview = usePreviewProposalTemplate(proposalId, work)

  const [draft, setDraft] = React.useState<ProposalTemplateOverrides | null>(
    null,
  )
  const [error, setError] = React.useState<string | null>(null)
  const [previewing, setPreviewing] = React.useState<ProposalDocumentDto | null>(
    null,
  )
  const [openSections, setOpenSections] = React.useState<
    ReadonlySet<EditableProseSection | 'jobTypeDescriptions'>
  >(() => new Set())

  function toggleOpen(key: EditableProseSection | 'jobTypeDescriptions'): void {
    setOpenSections((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const data = template.data
  // The draft starts as what is stored, not as what renders: saving must write
  // back only what somebody actually changed, or every section would become an
  // override the moment one was edited.
  const overrides = draft ?? data?.overrides ?? {}
  const isDirty = draft !== null

  function edit(next: ProposalTemplateOverrides): void {
    setError(null)
    setDraft(next)
  }

  /**
   * An empty description is not an override. Writing "" used to win over the
   * inherited text and print a blank. Clearing, or Reset, drops the key, and
   * drops the group once nothing in it is left.
   */
  function setJobTypeDescription(jobType: JobType, value: string): void {
    const descriptions = { ...overrides.jobTypeDescriptions }
    if (value.trim() === '') {
      delete descriptions[jobType]
    } else {
      descriptions[jobType] = value
    }
    if (Object.keys(descriptions).length === 0) {
      const next = { ...overrides }
      delete next.jobTypeDescriptions
      edit(next)
      return
    }
    edit({ ...overrides, jobTypeDescriptions: descriptions })
  }

  // `inherited`, not `resolved`: resolved already has this level's overrides
  // applied, so a section dropped by Reset would fall back to the very text
  // just discarded and Reset would look like it had done nothing.
  function sectionValue(section: EditableProseSection): ProseCopyDto {
    return overrides[section] ?? data?.inherited[section] ?? {
      title: null,
      paragraphs: [],
      bullets: [],
    }
  }

  /**
   * Which level a key's wording comes from, accounting for unsaved edits.
   *
   * `overriddenHere` is the caller's answer because the two kinds of key are
   * shaped differently — a prose section is a key of the overrides, a job
   * description is a key inside one of them — and a single string test would
   * silently be false for the latter, tagging a description somebody had just
   * rewritten as still being the default.
   */
  function sourceFor(
    key: TemplateSourceKey,
    overriddenHere: boolean,
  ): TemplateSource | undefined {
    if (draft === null) return data?.sources[key]
    // Mid-edit the saved sources are stale both ways: a key just edited
    // belongs to this level before it is saved, and one just Reset belongs to
    // the level below before that is saved either.
    if (overriddenHere) {
      return proposalId === undefined ? 'organization' : 'proposal'
    }
    return data?.inheritedSources[key]
  }

  async function handleSave(): Promise<void> {
    setError(null)
    try {
      await save.mutateAsync(overrides)
      setDraft(null)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  async function handlePreview(): Promise<void> {
    setError(null)
    try {
      setPreviewing(await preview.mutateAsync(overrides))
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  if (template.isPending) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
        </div>
      </section>
    )
  }

  if (template.isError || data === undefined) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4 text-center">
        <p className="text-sm font-medium">Wording could not be loaded</p>
        <p className="mt-1 text-sm text-muted-text">
          Check your connection and try again.
        </p>
      </section>
    )
  }

  /*
   * On one proposal, the opening narrative is the draft's intro field, which
   * replaces these paragraphs whenever it is written. Showing both would be
   * two editors for one section, one of them for text that never prints. A
   * proposal that already changed it here keeps it listed, so it can be Reset.
   */
  const sections = EDITABLE_PROSE_SECTIONS.filter(
    (section) =>
      proposalId === undefined ||
      section !== 'narrative' ||
      data.overrides.narrative !== undefined,
  )

  return (
    <section
      className={cn(
        'space-y-3',
        embedded ? null : 'rounded-lg border border-default bg-surface p-4',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="mt-0.5 text-sm text-muted-text">{blurb}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={preview.isPending}
            onClick={() => {
              void handlePreview()
            }}
          >
            {preview.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Eye className="h-3.5 w-3.5" />
            )}
            Preview
          </Button>
          <Button
            size="sm"
            disabled={!isDirty || save.isPending}
            onClick={() => {
              void handleSave()
            }}
          >
            {save.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Save
          </Button>
        </div>
      </div>

      {/*
        Said once, at the top. Somebody editing wording has one live question —
        does this rewrite what a client already has — and the answer is no.
      */}
      <p className="rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
        Changes apply to proposals built from here on. A proposal that has been
        sent keeps the wording it was sent with.
      </p>

      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      {sections.map((section) => (
        <SectionEditor
          key={section}
          idPrefix={idPrefix}
          section={section}
          value={sectionValue(section)}
          source={sourceFor(section, section in overrides)}
          canReset={section in overrides}
          open={openSections.has(section)}
          onToggle={() => {
            toggleOpen(section)
          }}
          onChange={(next) => {
            edit({ ...overrides, [section]: next })
          }}
          onReset={() => {
            const next = { ...overrides }
            // Deleting the key is what "use the level below" means — writing an
            // empty section would render a heading with nothing under it.
            delete next[section]
            edit(next)
          }}
        />
      ))}

      {/* Irrigation alone has no pruning to explain. */}
      {data.work === 'IRRIGATION' ? null : (
        <Disclosure
          title="What each pruning job type means"
          hint={`Printed beside the job badges, so a board member reading the schedule knows what they are agreeing to. “Other” covers access and clean-up rather than pruning, and is the one a client can never decline.${
            proposalId === undefined
              ? ' The same on proposals for trees and for trees and irrigation.'
              : ''
          }`}
          open={openSections.has('jobTypeDescriptions')}
          onToggle={() => {
            toggleOpen('jobTypeDescriptions')
          }}
          aside={
            overrides.jobTypeDescriptions === undefined ? null : (
              <SourceTag source={proposalId === undefined ? 'organization' : 'proposal'} />
            )
          }
        >
          {JOB_TYPES.map((jobType) => (
            <div key={jobType} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`${idPrefix}-job-${jobType}`}>
                  {JOB_TYPE_LABELS[jobType]}
                </Label>
                <div className="flex items-center gap-1">
                  <SourceTag
                    source={sourceFor(
                      `jobTypeDescriptions.${jobType}`,
                      overrides.jobTypeDescriptions?.[jobType] !== undefined,
                    )}
                  />
                  {overrides.jobTypeDescriptions?.[jobType] === undefined ? null : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setJobTypeDescription(jobType, '')
                      }}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset
                    </Button>
                  )}
                </div>
              </div>
              <textarea
                id={`${idPrefix}-job-${jobType}`}
                rows={2}
                className={TEXTAREA_CLASS}
                value={
                  overrides.jobTypeDescriptions?.[jobType] ??
                  data.inherited.jobTypeDescriptions[jobType]
                }
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setJobTypeDescription(jobType, event.target.value)
                }}
              />
            </div>
          ))}
        </Disclosure>
      )}

      <Dialog
        open={previewing !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewing(null)
        }}
      >
        <DialogContent className={cn('max-w-4xl p-0')}>
          <DialogHeader className="border-b border-default px-4 py-3">
            <DialogTitle>
              Preview — a sample proposal
              {isDirty ? ' (including unsaved changes)' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70svh] overflow-y-auto px-4 py-3">
            {previewing === null ? null : (
              <ProposalDocument document={previewing} variant="preview" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
